import { ReactElement } from 'react';
import { RemoteRecordHandler } from '@client/common/components/RemoteRecordHandler';
import { useDeviceType } from '@client/common/hooks';
import { config } from '@client/config';
import { useTranslate } from '@client/i18n/hooks';
import { ProductGallerySkeleton } from '@client/product/components/ProductGallery/ProductGallerySkeleton';
import { useProductGalleryEffect } from '@client/product/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useRouterPush, useUrlCreator } from '@client/routes/hooks';
import { getBrandUrlParams } from '@client/routes/utils/url';
import { Gallery } from '@lib/components/Gallery';
import { Props } from './ProductGallery.types';

export const ProductGallery = ({
  sku,
  productName,
  manufacturerLogo,
  manufacturerName,
  imageDisclaimer,
}: Props): ReactElement => {
  const deviceType = useDeviceType();
  const galleryRecord = useProductGalleryEffect(sku);
  const pushAction = useRouterPush();
  const urlCreator = useUrlCreator();
  const t = useTranslate();
  const { data: galleryData } = galleryRecord;

  const galleryPhotos = galleryData?.images?.map((img) => ({
    thumbnail: img.sizes.thumbnail,
    preview: img.sizes.preview,
    full: img.sizes.full,
  })) || null;

  const gallery360 = galleryData?.images360?.[0]?.sizes || null;

  const galleryVideos = galleryData?.videos || [];

  const manufacturerLogoUrl = (
    manufacturerLogo.find((resolution) => resolution.retina) || manufacturerLogo[0]
  )?.href || null;

  const manufacturerLink = `${urlCreator(ROUTES.brand)}${getBrandUrlParams(manufacturerName)}`;

  const handleManufacturerLogoClick = (): void => {
    pushAction(
      `${urlCreator(ROUTES.brand)}${getBrandUrlParams(manufacturerName)}`,
    );
  };

  return (
    <RemoteRecordHandler
      remoteRecord={galleryRecord}
      shouldCheckEmptyRecord
      customLoaderComponent={<ProductGallerySkeleton />}
    >
      <div className="ProductPhotos">
        <Gallery
          deviceType={deviceType}
          images={galleryPhotos}
          image360={gallery360}
          key={sku}
          videos={galleryVideos}
          title={productName}
          imageUrl={config.url.cdn.images}
          manufacturerLink={manufacturerLink}
          onManufacturerLogoClick={handleManufacturerLogoClick}
          manufacturerLogoUrl={manufacturerLogoUrl}
          imageDisclaimer={imageDisclaimer}
          text={{
            menuMobileImageLink: t('Images'),
            menuMobileVideoLink: t('Videos'),
            spinHint: t('Drag to spin. Click to zoom.'),
            spinHintMobile: t('Swipe to spin.'),
            zoomHint: t('Click to zoom.'),
            zoomHintMobile: t('Double tap to zoom.'),
          }}
        />
      </div>
    </RemoteRecordHandler>
  );
};
